import React, { useEffect } from 'react'; // ✅ Add useEffect
import { motion } from 'framer-motion';
import { IoArrowBack, IoCheckmarkCircle, IoRocketOutline, IoMedkitOutline } from 'react-icons/io5';
import { 
  GRAFT_SIZE_OPTIONS, 
  GRAFT_TYPE_OPTIONS,
  GRAFT_SIZE_OPTIONS_BY_TYPE // ✅ Import the new structure
} from '../../../data/conservativeCareDashboard';
import KitDurationSelection from './KitDurationSelection';
import Highlights from '../common/Highlights';

const AdvancedCareBuilder = ({
  orderStep,
  setOrderStep,
  selectedPatient,
  woundDetails,
  graftType,
  setGraftType,
  graftSize,
  setGraftSize,
  graftQuantity,
  setGraftQuantity,
  kitDuration,
  setKitDuration,
  ivrSubmitted,
  setOpenIvrModal,
  fetchDeliveryAddress,
  billingType,
  setDeliveryModalOpen,
  loading
}) => {
  // ✅ Get available sizes based on selected graft type
  const availableSizes = graftType 
    ? GRAFT_SIZE_OPTIONS_BY_TYPE[graftType] || GRAFT_SIZE_OPTIONS 
    : GRAFT_SIZE_OPTIONS;

  // ✅ Reset graft size when graft type changes
  useEffect(() => {
    if (graftType) {
      // Clear the selected size when type changes
      setGraftSize('');
    }
  }, [graftType, setGraftSize]);

  useEffect(() => {
    console.log('🔍 Advanced Care Builder State:', {
      graftType,
      graftSize,
      kitDuration,
      ivrSubmitted,
      loading,
      isDisabled: loading || !graftType || !graftSize || !graftQuantity
    });
  }, [graftType, graftSize, graftQuantity, ivrSubmitted, loading]);

  return (
    <motion.div 
      className="px-4 sm:px-6 lg:px-12 pb-8"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.3 }}
    >
      <div className="max-w-4xl mx-auto">
        <motion.div 
          className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-10 relative overflow-hidden"
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-indigo-400/10 to-blue-400/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>
          
          <div className="relative z-10">
            <motion.button 
              onClick={() => setOrderStep(2)}
              className="flex items-center gap-2 text-purple-600 dark:text-purple-400 hover:gap-3 transition-all font-bold mb-6 group"
              whileHover={{ x: -5 }}
            >
              <IoArrowBack className="text-xl group-hover:scale-110 transition-transform" /> 
              Back
            </motion.button>

            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center">
                    <motion.div 
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-black text-lg ${
                        orderStep >= step 
                          ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-lg' 
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                      }`}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: step * 0.1 }}
                      whileHover={{ scale: 1.1 }}
                    >
                      {orderStep > step ? <IoCheckmarkCircle className="text-2xl" /> : step}
                    </motion.div>
                    {step < 3 && (
                      <div className={`h-1 w-24 mx-2 rounded transition-all duration-500 ${
                        orderStep > step 
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}></div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-xs font-bold text-gray-600 dark:text-gray-400">
                <span>Patient</span>
                <span>Wound Details</span>
                <span className="text-purple-600 dark:text-purple-400">Select Graft</span>
              </div>
            </div>

            <motion.h2 
              className="text-3xl font-black text-gray-900 dark:text-white mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Step 3 of 3 - Select Graft
            </motion.h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">Choose your advanced care graft type and size</p>

            <motion.div 
              className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-2 border-purple-200 dark:border-purple-800 rounded-2xl p-6 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Highlights emoji="📋" color="teal" variant="bold" inline className="mb-4">
                <h3 className="text-lg font-black text-gray-900 dark:text-white">
                  Order Summary
                </h3>
              </Highlights>
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl p-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white font-black text-lg">
                    {selectedPatient?.first_name?.charAt(0)}{selectedPatient?.last_name?.charAt(0)}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Patient</p>
                    <p className="text-sm font-black text-gray-900 dark:text-white">
                      {selectedPatient?.first_name} {selectedPatient?.last_name}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl p-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
                    <IoMedkitOutline className="text-white text-xl" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Wound Type</p>
                    <p className="text-sm font-black text-gray-900 dark:text-white">{woundDetails.type}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* <KitDurationSelection kitDuration={kitDuration} setKitDuration={setKitDuration} /> */}

            {/* GRAFT TYPE SELECTION */}
            <motion.div 
              className="mb-6 bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border-2 border-indigo-300 dark:border-indigo-700 rounded-2xl p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-black">
                  1
                </div>
                <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase">
                  Graft Type
                </h3>
                <span className="text-red-500 text-xl">*</span>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                  Select Graft Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={graftType}
                  onChange={(e) => setGraftType(e.target.value)}
                  className="w-full px-5 py-4 bg-white dark:bg-gray-800 border-2 border-indigo-300 dark:border-indigo-600 rounded-xl text-gray-900 dark:text-white focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold appearance-none cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-500"
                >
                  <option value="">Select graft type...</option>
                  {GRAFT_TYPE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </motion.div>

            {/* GRAFT SIZE SELECTION - ✅ UPDATED */}
            <motion.div 
              className="mb-8 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-2 border-purple-300 dark:border-purple-700 rounded-2xl p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-black">
                  2
                </div>
                <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase">
                  Graft Size
                </h3>
                <span className="text-red-500 text-xl">*</span>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                  Select Graft Size <span className="text-red-500">*</span>
                </label>
                
                {/* ✅ Show helper text if no graft type selected */}
                {!graftType && (
                  <p className="text-sm text-amber-600 dark:text-amber-400 mb-2 font-medium">
                    Please select a graft type first
                  </p>
                )}
                
                <select
                  value={graftSize}
                  onChange={(e) => setGraftSize(e.target.value)}
                  disabled={!graftType} // ✅ Disable if no type selected
                  className="w-full px-5 py-4 bg-white dark:bg-gray-800 border-2 border-purple-300 dark:border-purple-600 rounded-xl text-gray-900 dark:text-white focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-semibold appearance-none cursor-pointer hover:border-purple-400 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {graftType ? 'Select graft size...' : 'Select graft type first'}
                  </option>
                  {availableSizes.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                
                {/* ✅ Show count of available sizes */}
                {graftType && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {availableSizes.length} size{availableSizes.length !== 1 ? 's' : ''} available for {graftType}
                  </p>
                )}
              </div>
            </motion.div>

            <motion.div className="mb-6 ...">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-black">3</div>
                <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase">Graft Quantity</h3>
                <span className="text-red-500 text-xl">*</span>
              </div>
              <select
                value={graftQuantity}
                onChange={(e) => setGraftQuantity(e.target.value)}
                className="w-full px-5 py-4 bg-white dark:bg-gray-800 border-2 border-green-300 dark:border-green-600 rounded-xl ..."
              >
                <option value="">Select quantity...</option>
                {[...Array(10)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                ))}
              </select>
            </motion.div>

            <motion.div 
              className="flex justify-between gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <motion.button 
                onClick={() => setOrderStep(2)}
                className="px-8 py-4 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-2xl font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all flex items-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <IoArrowBack />
                Back
              </motion.button>
              <motion.button 
                onClick={async () => {
                  if (billingType === 'provider') {
                  // Skip IVR — go straight to delivery/order
                  await fetchDeliveryAddress();
                  setDeliveryModalOpen(true);
                } else {
                  // ProMed — normal IVR flow
                  if (!ivrSubmitted) {
                    setOpenIvrModal(true);
                  } else {
                    await fetchDeliveryAddress();
                    setDeliveryModalOpen(true);
                  }
                }
                }}
                disabled={loading || !graftType || !graftSize || !graftQuantity}
                className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-2xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl flex items-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {loading ? (
                  <>
                    <motion.div
                      className="w-5 h-5 border-3 border-white border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    Submitting...
                  </>
                ) : (
                  <>
                    <IoRocketOutline className="text-xl" />
                    Submit Order
                  </>
                )}
              </motion.button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default AdvancedCareBuilder;